/**
 * Derived conversation-status selectors.
 *
 * Single source of truth for "is Sara currently working on my message?", used
 * to drive the typing indicator.
 */

import {
  MessageRole,
  TurnPhase,
  type Message,
  type TurnState,
} from '@/types/chat';

/**
 * True when the newest message is a LIVE user message with no assistant
 * response after it yet — i.e. we're awaiting Sara's reply.
 *
 * This is a pure DERIVATION on purpose. It is self-correcting (the instant any
 * assistant prose, tool card, or system message lands, the newest message is no
 * longer the user's, so it flips off) and it is agnostic to turn-boundary
 * complexity — in particular the tool→prose gap where a tool card is deferred
 * until the prose arrives, and the backend's multi-`agent_end` turns. There is
 * therefore no stuck-state and no need to replicate the backend's turn debounce.
 *
 * "Live" is read from EXPLICIT provenance (`Message.restored`), not the id shape
 * (live ids are now the outbox's opaque uuid) nor the delivery `status` field.
 * A resumed conversation that merely ends on a user turn is `restored`, so it
 * correctly does NOT show a responding indicator.
 *
 * ROLE (since turn_state): the FALLBACK leg of `shouldShowTypingDots` — the
 * whole signal only when the backend predates the turn_state contract (null
 * turnState), and the "message sent, thinking not yet acked" bridge otherwise.
 * Its known blind spot (assistant commentary before a tool call flips it off
 * mid-turn) is exactly what the backend-authoritative phase covers.
 */
export function isAwaitingAssistantReply(messages: Message[]): boolean {
  const last = messages[messages.length - 1];
  return last?.role === MessageRole.USER && !last.restored && !last.abandoned;
}

/**
 * [FB36] Call-end settle for the awaiting derivation above.
 *
 * Ending a voice call is the ONE event that breaks the derivation's
 * self-correcting premise ("some message always lands and flips it off"):
 * the in-flight turn dies with the call — the sideband closes and the
 * rebuilt text session does not resume the response — so a trailing live
 * user message (typically the call's last voice transcription) would keep
 * the typing dots up forever. Stamp it `abandoned` at the call-end
 * boundary instead of special-casing the derivation itself.
 *
 * Returns the SAME array reference when there is nothing to settle (last
 * message absent / not a user's / restored / already abandoned) so the
 * setMessages caller bails without a render.
 */
export function settleAbandonedTurn(messages: Message[]): Message[] {
  const last = messages[messages.length - 1];
  if (last?.role !== MessageRole.USER || last.restored || last.abandoned) {
    return messages;
  }
  return [...messages.slice(0, -1), { ...last, abandoned: true }];
}

/**
 * THE "is the assistant working?" selector — drives the typing dots. Pure;
 * unit-tested. (Until 2026-07-13 this also fed a "Responding.." status line
 * above the composer — a v2-design relic, retired; live activity copy now
 * lives in the Thinking timeline's "Working…" header.)
 *
 * Two signal sources, by design:
 *  - `turnState` — the backend-authoritative phase (TURN_STATE event). Truth
 *    whenever present: it spans the commentary→tool→generation sequence that
 *    message-shape inference cannot see.
 *  - `isAwaitingAssistantReply(messages)` — the legacy derivation. Sole signal
 *    when turnState is null (old backend = capability gate), and the bridge
 *    for the send→thinking round-trip gap on new backends.
 *
 * Dots rule: awaiting || THINKING || USING_TOOL. RESPONDING is deliberately
 * EXCLUDED — by the time it fires, prose has landed (text mode) or is
 * streaming, so keeping dots up would linger ~1s past the answer (idle rides
 * the backend's turn-flush debounce).
 */
export function shouldShowTypingDots(
  turnState: TurnState | null,
  messages: Message[]
): boolean {
  const awaiting = isAwaitingAssistantReply(messages);
  const phase = turnState?.phase ?? null;
  return (
    awaiting || phase === TurnPhase.THINKING || phase === TurnPhase.USING_TOOL
  );
}
